/**
 * DocumentsTab — Shows documents associated with an entity
 *
 * Lists linked documents with file type indicators and metadata.
 * DUMB FRONTEND: Document data comes from backend GraphQL.
 */

import {
  Stack,
  Text,
  Group,
  Badge,
  Center,
  Loader,
  Alert,
  Paper,
  ThemeIcon,
} from '@mantine/core';
import {
  IconFile,
  IconFileTypePdf,
  IconFileText,
  IconFileSpreadsheet,
} from '@tabler/icons-react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useTenantId } from '@/auth/AuthContext';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';

// ============================================================================
// GRAPHQL QUERY
// ============================================================================

// Uses backend listDocuments resolver (tenant-scoped, paginated).
// Per-entity document filtering is a future enhancement.
const ENTITY_DOCUMENTS_QUERY = gql`
  query EntityDocuments($tenantId: String!, $limit: Int) {
    listDocuments(tenantId: $tenantId, limit: $limit) {
      nodes {
        documentId
        documentName
        description
        currentStatus
        fileExtension
        fileSizeBytes
        createdAt
        createdByActor {
          id
          displayName
        }
      }
      totalCount
      hasNextPage
    }
  }
`;

// ============================================================================
// HELPERS
// ============================================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getFileIcon(fileType: string | undefined): React.ComponentType<any> {
  switch (fileType?.toLowerCase()) {
    case 'pdf':
      return IconFileTypePdf;
    case 'txt':
    case 'md':
      return IconFileText;
    case 'csv':
      return IconFileSpreadsheet;
    default:
      return IconFile;
  }
}

function formatFileSize(bytes: number | undefined): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

// ============================================================================
// COMPONENT
// ============================================================================

interface DocumentsTabProps {
  entity: PrimitiveItem | null;
  primitiveType: string;
}

interface DocumentRecord {
  documentId: string;
  documentName: string;
  description?: string;
  currentStatus?: string;
  fileExtension?: string;
  fileSizeBytes?: string;
  createdAt?: string;
  createdByActor?: { id: string; displayName?: string };
}

export function DocumentsTab({ entity, primitiveType }: DocumentsTabProps) {
  const entityId = entity?.id as string | undefined;
  const tenantId = useTenantId();

  const { data, loading, error } = useQuery<{
    listDocuments: {
      nodes: DocumentRecord[];
      totalCount: number;
      hasNextPage: boolean;
    };
  }>(ENTITY_DOCUMENTS_QUERY, {
    variables: {
      tenantId,
      limit: 50,
    },
    skip: !entityId || !tenantId,
    fetchPolicy: 'cache-and-network',
  });

  const documents = data?.listDocuments?.nodes ?? [];

  if (!entityId) {
    return (
      <Center py="xl">
        <Text c="dimmed" size="sm">Select an entity to view its documents.</Text>
      </Center>
    );
  }

  if (loading && documents.length === 0) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Loader size="sm" />
          <Text c="dimmed" size="sm">Loading documents...</Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="yellow" title="Documents">
        <Text size="sm">Document listing is not yet available for this entity type.</Text>
      </Alert>
    );
  }

  if (documents.length === 0) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <ThemeIcon size="xl" variant="light" color="gray">
            <IconFile size={24} />
          </ThemeIcon>
          <Text c="dimmed" size="sm">No documents attached to this entity.</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="sm" fw={600}>Documents</Text>
        <Badge size="xs" variant="light">{documents.length}</Badge>
      </Group>

      {documents.map((doc) => {
        const FileIcon = getFileIcon(doc.fileExtension);
        const sizeNum = doc.fileSizeBytes ? Number.parseInt(doc.fileSizeBytes, 10) : undefined;
        return (
          <Paper key={doc.documentId} withBorder p="sm" radius="sm">
            <Group gap="sm">
              <ThemeIcon size="lg" variant="light" color="blue">
                <FileIcon size={18} />
              </ThemeIcon>
              <Stack gap={2} style={{ flex: 1 }}>
                <Text size="sm" fw={500}>{doc.documentName}</Text>
                <Group gap="xs">
                  {doc.fileExtension && (
                    <Badge size="xs" variant="outline">{doc.fileExtension.toUpperCase()}</Badge>
                  )}
                  {sizeNum && (
                    <Text size="xs" c="dimmed">{formatFileSize(sizeNum)}</Text>
                  )}
                  {doc.currentStatus && (
                    <Badge size="xs" variant="light">{doc.currentStatus}</Badge>
                  )}
                </Group>
              </Stack>
            </Group>
          </Paper>
        );
      })}
    </Stack>
  );
}
